import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Play, Pause, Bell, Loader2, CalendarClock, Eye } from "lucide-react";
import { Button } from "../ui/button";
import { platformApi } from "../../lib/api";
import EmailBuilder from "../marketing/EmailBuilder";
import PopupModal from "./PopupModal";

const inp =
  "w-full h-8 px-2.5 rounded-md border border-white/10 bg-[#0F1623] text-[12.5px] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/30";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function toLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function PlatformPopups() {
  const [popups, setPopups] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [drafts, setDrafts] = useState({}); // per-popup pending start/end date edits
  const [previewPopup, setPreviewPopup] = useState(null);

  const reload = async () => {
    try {
      const list = await platformApi.popups.list();
      setPopups(list);
    } catch {
      toast.error("Failed to load popups");
    }
  };
  useEffect(() => { reload(); }, []);

  const openNew = () => { setEditingId(null); setBuilderOpen(true); };
  const openEdit = (id) => { setEditingId(id); setBuilderOpen(true); };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete popup "${name}"? This can't be undone.`)) return;
    setBusyId(id);
    try {
      await platformApi.popups.remove(id);
      toast.success("Popup deleted");
      reload();
    } catch {
      toast.error("Failed to delete popup");
    } finally { setBusyId(null); }
  };

  const toggleActive = async (p) => {
    setBusyId(p.id);
    try {
      if (p.status === "active") {
        await platformApi.popups.deactivate(p.id);
        toast.success("Popup deactivated");
      } else {
        await platformApi.popups.activate(p.id);
        toast.success(`"${p.name}" is now the active popup`);
      }
      reload();
    } catch {
      toast.error("Failed to update popup status");
    } finally { setBusyId(null); }
  };

  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));

  const saveDates = async (p) => {
    const d = drafts[p.id] || {};
    setBusyId(p.id);
    try {
      await platformApi.popups.update(p.id, {
        startAt: d.startAt !== undefined ? (d.startAt ? new Date(d.startAt).toISOString() : null) : undefined,
        endAt: d.endAt !== undefined ? (d.endAt ? new Date(d.endAt).toISOString() : null) : undefined,
      });
      toast.success("Schedule saved");
      setDrafts((all) => { const c = { ...all }; delete c[p.id]; return c; });
      reload();
    } catch {
      toast.error("Failed to save schedule");
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-5" data-testid="platform-popups-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[16px] font-semibold text-white">Popup Notifications</h2>
          <p className="text-[12.5px] text-slate-400 mt-0.5">Shown to your company customers when they log into their account. Only one popup can be active at a time. Use Preview to see it without publishing.</p>
        </div>
        <Button onClick={openNew} data-testid="popup-new-btn" className="h-9 gap-1.5 bg-cyan-600 hover:bg-cyan-500 text-white">
          <Plus className="h-4 w-4" /> New Popup
        </Button>
      </div>

      {popups === null ? (
        <div className="py-16 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>
      ) : popups.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-[#0F1623] py-16 text-center" data-testid="popups-empty-state">
          <Bell className="h-8 w-8 text-slate-600 mx-auto mb-3" />
          <p className="text-[13.5px] text-slate-400">No popups yet. Build one with the email builder to announce something to the platform team.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {popups.map((p) => {
            const draft = drafts[p.id] || {};
            const startVal = draft.startAt !== undefined ? draft.startAt : toLocalInputValue(p.startAt);
            const endVal = draft.endAt !== undefined ? draft.endAt : toLocalInputValue(p.endAt);
            const dirty = draft.startAt !== undefined || draft.endAt !== undefined;
            return (
              <div key={p.id} className="rounded-xl border border-white/10 bg-[#0F1623] p-4" data-testid={`popup-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-[14px] text-white truncate" data-testid="popup-card-name">{p.name}</div>
                  <span
                    data-testid="popup-card-status"
                    className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${p.status === "active" ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-600/30 text-slate-400"}`}
                  >
                    {p.status === "active" ? "Active" : "Inactive"}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="text-[10.5px] text-slate-500 flex items-center gap-1"><CalendarClock className="h-3 w-3" /> Start date</label>
                    <input type="datetime-local" value={startVal} onChange={(e) => setDraft(p.id, { startAt: e.target.value })} data-testid="popup-start-input" className={`${inp} mt-1`} />
                  </div>
                  <div>
                    <label className="text-[10.5px] text-slate-500 flex items-center gap-1"><CalendarClock className="h-3 w-3" /> End date</label>
                    <input type="datetime-local" value={endVal} onChange={(e) => setDraft(p.id, { endAt: e.target.value })} data-testid="popup-end-input" className={`${inp} mt-1`} />
                  </div>
                </div>
                {dirty && (
                  <button onClick={() => saveDates(p)} disabled={busyId === p.id} data-testid="popup-save-dates-btn" className="mt-2 text-[12px] font-medium text-cyan-400 hover:text-cyan-300">
                    Save schedule
                  </button>
                )}
                {!dirty && (p.startAt || p.endAt) && (
                  <p className="mt-2 text-[11px] text-slate-500">{fmt(p.startAt)} &rarr; {fmt(p.endAt)}</p>
                )}

                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <button onClick={() => openEdit(p.id)} data-testid="popup-edit-btn" className="h-8 px-3 rounded-md text-[12.5px] font-medium text-slate-200 bg-white/5 hover:bg-white/10 flex items-center gap-1.5">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button onClick={() => setPreviewPopup(p)} data-testid="popup-preview-btn" className="h-8 px-3 rounded-md text-[12.5px] font-medium text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 flex items-center gap-1.5">
                    <Eye className="h-3.5 w-3.5" /> Preview
                  </button>
                  <button onClick={() => toggleActive(p)} disabled={busyId === p.id} data-testid="popup-toggle-active-btn"
                    className={`h-8 px-3 rounded-md text-[12.5px] font-medium flex items-center gap-1.5 ${p.status === "active" ? "bg-amber-500/15 text-amber-400 hover:bg-amber-500/25" : "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"}`}>
                    {busyId === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : p.status === "active" ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                    {p.status === "active" ? "Deactivate" : "Activate"}
                  </button>
                  <button onClick={() => remove(p.id, p.name)} disabled={busyId === p.id} data-testid="popup-delete-btn" className="h-8 px-3 rounded-md text-[12.5px] font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 flex items-center gap-1.5">
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <EmailBuilder
        open={builderOpen}
        popupMode
        editPopupId={editingId}
        onClose={() => setBuilderOpen(false)}
        onSaved={() => { setBuilderOpen(false); reload(); }}
      />
      {previewPopup && <PopupModal popup={previewPopup} onClose={() => setPreviewPopup(null)} />}
    </div>
  );
}
